import { useCallback, useEffect, useState } from 'react';

import { useAuthStore, useUserStore } from '../../store';
import Table from '../../components/table/table.component';
import { hasActiveSubscription } from '../../utils/user-subscription.util';
import { useNavigation } from '../../hooks/navigation.hook';
import SearchInput from '../../components/search-input/search-input';
import View from '../../components/view/view';
import { PermissionCallbackBoolean } from '../../types/permission-callback';
import { RolePermission } from '../../types/operator.type';
import { useSettingsStore } from '../../store/settings-store';

import styles from './users.module.scss';

const UsersView = () => {
    const { navigate } = useNavigation();

    const { theme } = useSettingsStore();
    const { fetchAll, list: usersList } = useUserStore();
    const { permissions } = useAuthStore();

    const [rows, setRows] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        count: 10,
    });
    const [filter, setFilter] = useState('');

    useEffect(() => {
        const params: {
            count: number;
            page: number;
            search?: string;
        } = {
            count: pagination.count,
            page: pagination.currentPage,
        };
        if (filter) {
            params.search = filter;
        } else {
            params.count = 10;
        }
        listUsers(params, permissions);
    }, [pagination.currentPage, filter, permissions]);

    useEffect(() => {
        mapUsers();
    }, [usersList]);

    const canRead: PermissionCallbackBoolean = useCallback(
        (permissions: RolePermission[]): boolean => {
            return Boolean(
                permissions.find((per) => per.name === 'read-users')
            );
        },
        []
    );

    const listUsers = useCallback(
        (params: any, permissions: RolePermission[]) => {
            if (!canRead(permissions)) return;
            fetchAll(params);
        },
        []
    );

    const updateFilter = (filter: string) => {
        setFilter(filter.trim());
        setPagination((prev) => ({ ...prev, currentPage: 1, count: 10 }));
    };

    const mapUsers = () => {
        setPagination((prev) => ({
            ...prev,
            currentPage: Number(usersList.page) ?? 1,
            totalPages:
                usersList.count === 0
                    ? 1
                    : Math.ceil(usersList.total / pagination.count),
        }));
        const userRows = usersList.result.map((user) => {
            return {
                id: user.id,
                data: [
                    { type: 'number', value: user.id },
                    {
                        type: 'string',
                        value: `${user.firstName} ${user.lastName ?? ''}`,
                    },
                    {
                        type: 'string',
                        value: user.email ?? '-',
                    },
                    {
                        type: 'string',
                        value: `${user.documentType ?? '?'}-${
                            user.document ?? '?'
                        }`,
                    },
                    {
                        type: 'status',
                        value: hasActiveSubscription(
                            user.hasActiveSubscription
                        ),
                    },
                    {
                        type: 'string',
                        value: user.companyName ?? '-',
                    },
                ],
            };
        });
        setRows(userRows);
    };

    const onPageChange = (page: number) => {
        if (page < 1) return;
        setPagination((prev) => ({ ...prev, currentPage: page }));
    };

    return (
        <View
            theme={theme}
            permissions={permissions}
            requiredPermission="read-users"
        >
            <div className={'viewContent'}>
                <h1 className={'view-title'}>Lista de usuarios</h1>
                <div className={'flex align-center'} style={{ gap: 10 }}>
                    <SearchInput
                        onSearch={updateFilter}
                        className={styles.searchInput}
                    />
                </div>
                <Table
                    header={[
                        'ID',
                        'Nombre',
                        'Correo electrónico',
                        'Cédula',
                        'Suscripción',
                        'Compañía',
                    ]}
                    data={rows}
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={onPageChange}
                    onItemSelected={(id) => navigate(`/users/${id}`)}
                />
            </div>
        </View>
    );
};

export default UsersView;
